/**
 * `/try` — a focused paid-ad landing page (e.g. Facebook). Unlike the full
 * homepage it carries NO site nav (see `isOwnChrome` in App.tsx) and no agent
 * rail: just one value-prop headline and the guest dock, so a cold ad visitor
 * can try Uno in a single tap. The dock is the SAME component + guest-mint flow
 * the homepage uses (reCAPTCHA-gated `/guest/start`), so there is no second
 * code path to keep in sync. When guest chat is disabled (local dev / a stage
 * with the flag off) it falls back to the create-account CTA.
 *
 * Point ad traffic here, NOT at `/guest` — `/guest` needs a dock-minted guest
 * session and redirects a cold, session-less visitor back to `/`.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AGENTS } from '../agents/registry.ts';
import GuestDock from '../components/GuestDock.tsx';
import { GUEST_CHAT_ENABLED } from '../config/features.ts';
import './Try.css';

const MOBILE_QUERY = '(max-width: 720px)';

/** Mirror of Hero's mobile-layout hook (autofocus only on desktop). */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function Try() {
  const isMobile = useIsMobile();

  return (
    <section className="om-try">
      <div className="om-try__inner">
        <Link to="/" className="om-try__brand" aria-label="OculusMind.AI home">
          Oculus<span className="om-try__brand-accent">Mind</span>.AI
        </Link>

        <img
          className="om-try__art"
          src={AGENTS.uno.image}
          alt="Uno, OculusMind's perception-core AI agent"
          width={128}
          height={128}
          loading="eager"
        />

        <h1 className="om-try__headline">
          Chat with an AI agent — <span className="om-try__nowrap">free, no sign-up.</span>
        </h1>
        <p className="om-try__sub">
          Ask Uno anything — today&rsquo;s weather, stock market updates, or the open web. Get a
          real, sourced answer in seconds.
        </p>

        {GUEST_CHAT_ENABLED ? (
          <div className="om-try__dock">
            <GuestDock onEngage={() => undefined} autoFocus={!isMobile} isMobile={isMobile} />
          </div>
        ) : (
          <Link to="/auth/signup" className="om-try__cta">
            Create a free account
          </Link>
        )}

        <p className="om-try__trust">No account · No credit card · Free to try</p>

        <ul className="om-try__points" aria-label="What you can do">
          <li>Searches the live web and cites its sources</li>
          <li>Reads real-time weather and stock market data</li>
          <li>Reads and summarizes any web page you share</li>
        </ul>

        <p className="om-try__foot">
          Want memory, files, and bigger daily limits?{' '}
          <Link to="/auth/signup">Create a free account</Link>.
        </p>
      </div>
    </section>
  );
}
